<template>
  <div>
    <div class="d-flex align-center flex-wrap mb-1" style="gap: 8px;">
      <span class="text-caption font-weight-medium">{{ label || t('admConfig.conditions.title') }}</span>
      <v-btn size="x-small" variant="tonal" prepend-icon="mdi-plus" @click="addClause">
        {{ t('admConfig.conditions.add') }}
      </v-btn>
    </div>

    <div class="text-caption text-medium-emphasis mb-2">
      {{ hint || t('admConfig.conditions.hint') }}
    </div>

    <div v-if="!clauses.length" class="text-caption text-disabled mb-1">
      {{ t('admConfig.conditions.empty') }}
    </div>

    <v-row v-for="(clause, index) in clauses" :key="index" dense align="center">
      <v-col cols="12" md="4">
        <v-select
          v-model="clause.field"
          :items="fieldItems"
          item-title="title"
          item-value="value"
          :label="t('admConfig.conditions.field')"
          density="compact"
          hide-details
          @update:model-value="onFieldChange(clause)"
        />
      </v-col>
      <v-col cols="12" md="3">
        <v-select
          v-model="clause.op"
          :items="operatorItems"
          item-title="title"
          item-value="value"
          :label="t('admConfig.conditions.operator')"
          density="compact"
          hide-details
          @update:model-value="emitValue"
        />
      </v-col>
      <v-col cols="11" md="4">
        <!-- "compilato": non c'è un valore da confrontare, solo il verso della condizione -->
        <v-select
          v-if="clause.op === 'isSet'"
          v-model="clause.isSet"
          :items="isSetItems"
          item-title="title"
          item-value="value"
          :label="t('admConfig.conditions.value')"
          density="compact"
          hide-details
          @update:model-value="emitValue"
        />
        <v-combobox
          v-else-if="clause.op === 'values'"
          v-model="clause.values"
          :items="suggestionsFor(clause.field)"
          :label="t('admConfig.conditions.values')"
          multiple
          chips
          closable-chips
          density="compact"
          hide-details
          @update:model-value="emitValue"
        />
        <!-- Checkbox: il valore utile è solo sì/no, e scriverlo a mano porta a "si", "1", "vero" -->
        <v-select
          v-else-if="isCheckbox(clause.field)"
          v-model="clause.value"
          :items="boolItems"
          item-title="title"
          item-value="value"
          :label="t('admConfig.conditions.value')"
          density="compact"
          hide-details
          @update:model-value="emitValue"
        />
        <v-combobox
          v-else
          v-model="clause.value"
          :items="suggestionsFor(clause.field)"
          :label="t('admConfig.conditions.value')"
          density="compact"
          hide-details
          @update:model-value="emitValue"
        />
      </v-col>
      <v-col cols="1" class="text-right">
        <v-btn icon variant="text" size="x-small" color="error" @click="removeClause(index)">
          <v-icon size="small">mdi-delete</v-icon>
        </v-btn>
      </v-col>
    </v-row>
  </div>
</template>

<script setup>
/**
 * Editor delle condizioni di attivazione (cfg.json -> conditionalOn delle azioni IA).
 *
 * Grammatica: lista di clausole in AND, ognuna { field, value | notValue | values | isSet }.
 * Il modello esterno è SEMPRE quell'array serializzato: chi lo usa lo scrive tale e quale nel file.
 * Lo stato interno tiene in più un `op`, perché l'operatore in JSON è la chiave stessa e in
 * un'interfaccia serve un campo a parte.
 *
 * Non è l'editor delle condizioni dei CAMPI (FieldsEditor.vue): quella grammatica è a condizione
 * singola e con confronto stretto, e non va mescolata con questa.
 */
import { ref, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const props = defineProps({
  // Array di clausole già in forma cfg.json (o null/undefined quando non ce ne sono)
  modelValue: { type: Array, default: () => [] },
  // Campi su cui si può condizionare: [{ name, label, type, options }]
  fields: { type: Array, default: () => [] },
  label: { type: String, default: '' },
  hint: { type: String, default: '' }
})

const emit = defineEmits(['update:modelValue'])

const clauses = ref([])

// JSON -> stato dell'interfaccia
const toDraft = (list) => (Array.isArray(list) ? list : []).filter(c => c && typeof c === 'object').map(c => {
  if (c.isSet !== undefined) return { field: c.field || '', op: 'isSet', isSet: c.isSet !== false, value: '', values: [] }
  if (c.notValue !== undefined) return { field: c.field || '', op: 'notValue', value: c.notValue, values: [], isSet: true }
  if (Array.isArray(c.values)) return { field: c.field || '', op: 'values', value: '', values: [...c.values], isSet: true }
  return { field: c.field || '', op: 'value', value: c.value ?? '', values: [], isSet: true }
})

// Stato dell'interfaccia -> JSON. Le clausole senza campo non finiscono nel file: sono righe
// appena aggiunte e ancora da compilare, non una condizione "su niente".
const toJson = () => clauses.value
  .filter(c => c.field)
  .map(c => {
    if (c.op === 'isSet') return { field: c.field, isSet: c.isSet !== false }
    if (c.op === 'values') return { field: c.field, values: (c.values || []).map(v => asScalar(v)) }
    if (c.op === 'notValue') return { field: c.field, notValue: asScalar(c.value) }
    return { field: c.field, value: asScalar(c.value) }
  })

// Rete di sicurezza per le combobox: un item selezionato può arrivare come oggetto { title, value }
const asScalar = (value) => (value && typeof value === 'object' ? (value.value ?? value.title ?? '') : value)

watch(() => props.modelValue, (list) => {
  // Non ricostruire la bozza mentre è questo componente a emettere: si perderebbe il focus
  if (JSON.stringify(toJson()) === JSON.stringify(list || [])) return
  clauses.value = toDraft(list)
}, { immediate: true, deep: true })

const emitValue = () => emit('update:modelValue', toJson())

const addClause = () => {
  clauses.value.push({ field: '', op: 'value', value: '', values: [], isSet: true })
}

const removeClause = (index) => {
  clauses.value.splice(index, 1)
  emitValue()
}

// Cambiato campo: il valore vecchio appartiene a un altro dominio, tenerlo confonde e basta
const onFieldChange = (clause) => {
  clause.value = isCheckbox(clause.field) ? true : ''
  clause.values = []
  emitValue()
}

const fieldItems = computed(() => {
  const seen = new Map()
  props.fields.forEach(f => {
    if (f?.name && !seen.has(f.name)) seen.set(f.name, { title: `${f.label || f.name} (${f.name})`, value: f.name })
  })
  return [...seen.values()]
})

const fieldByName = (name) => props.fields.find(f => f.name === name)

const isCheckbox = (name) => fieldByName(name)?.type === 'checkbox'

// Valori proposti: le opzioni del campo scelto, come già fa l'editor dei campi
const suggestionsFor = (name) => {
  const field = props.fields.find(f => f.name === name && Array.isArray(f.options))
  return (field?.options || []).map(o => String(o.value ?? o))
}

const operatorItems = computed(() => [
  { title: t('admConfig.conditions.opEquals'), value: 'value' },
  { title: t('admConfig.conditions.opNotEquals'), value: 'notValue' },
  { title: t('admConfig.conditions.opIn'), value: 'values' },
  { title: t('admConfig.conditions.opIsSet'), value: 'isSet' }
])

const isSetItems = computed(() => [
  { title: t('admConfig.conditions.filled'), value: true },
  { title: t('admConfig.conditions.emptyValue'), value: false }
])

const boolItems = computed(() => [
  { title: t('admConfig.conditions.yes'), value: true },
  { title: t('admConfig.conditions.no'), value: false }
])
</script>
